/*******************************************************
* ButtonRow
*
* A row of buttons with the standard gap, for form footers, confirm dialogs and
* panel actions, in both looks. Wraps on narrow screens instead of overflowing.
*
* Key Features:
* - align: where the buttons sit in the row (default end, the deciding action last)
* - sx: only for space around the row (mt, pt) or a divider; the gap comes from tokens.space.buttonGap
*
* Usage Example:
* ```tsx
* <ButtonRow>
*     <Button onClick={onCancel}>Cancel</Button>
*     <Button variant="contained" onClick={onSave}>Save</Button>
* </ButtonRow>
* <ButtonRow align="between" sx={{ mt: tokens.space.section }}>...</ButtonRow>
* ```
********************************************************/

//########## IMPORTS ##########
// #region IMPORTS
import type { ReactNode } from 'react';
import { Box, type BoxProps, type SxProps, type Theme } from '@mui/material';
import { tokens } from '@/src/theme/tokens';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface ButtonRowProps extends Omit<BoxProps, 'children'> {
    children: ReactNode;
    align?: 'start' | 'center' | 'end' | 'between';
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const JUSTIFY = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
} as const;
//#endregion


export default function ButtonRow({ children, align = 'end', sx, ...rest }: ButtonRowProps) {
    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <Box
            {...rest}
            sx={[
                {
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: tokens.space.buttonGap,
                    justifyContent: JUSTIFY[align],
                },
                ...(Array.isArray(sx) ? sx as unknown[] : [sx]),
            ] as SxProps<Theme>}
        >
            {children}
        </Box>
    );
    //#endregion
}
